import { useMedicalChronology } from "@/components/MedicalChronology/contexts/MedicalChronologyContext";
import { useMedicalChronologySearch } from "@/components/MedicalChronology/contexts/MedicalChronologySearchContext";
import { getCollapsibleVisitTypeIds } from "@/constants";
import { useFocusSearchOnCmdF } from "@/hooks/ui/useFocusSearchOnCmdF";
import type {
  MedVisitBulkCopyResponse,
  MedVisitCitation,
  MedVisitCreateRequest,
  MedVisitUpdateRequest,
} from "@/services/medChronServiceV2.types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import type { VirtuosoHandle } from "react-virtuoso";

import { useMedChronServices } from "../../../hooks/useMedChronServices";
import { useVisits } from "../../../hooks/useVisits";
import { useUserCustomizationPreferencesStore } from "../../../store/userCustomizationPreferencesStore";
import { getErrorMessage } from "../../../utils/errorUtils";
import { DeleteConfirmModals } from "./DeleteConfirmModals";
import { NewVisitFormModal } from "./NewVisitFormModal";
import type { VisitHeaderSaveValues } from "./VisitHeaderForm.types";
import { NEW_VISIT_CITATIONS_ERROR_KEY, VISIT_TYPE_ERROR_KEY } from "./Visits.types";
import { VisitsBulkCopyModal } from "./VisitsBulkCopyModal";
import { VisitsView } from "./VisitsView";
import {
  DeleteDetailConfirm,
  EditingVisitDetailState,
  NewVisitValues,
  VisitDisplay,
} from "./types";
import type { BulkEditSaveValues } from "./types/bulkEdit";
import {
  applyBulkVisitPatchToList,
  removeDeletedVisitsFromList,
} from "./utils/applyBulkVisitListUpdates";
import { buildBulkVisitUpdatePayload } from "./utils/bulkEditForm";
import {
  areVisitDetailEditsSemanticallyNoop,
  normalizeVisitDetailCategory,
} from "./utils/medVisitDetailNormalize";
import {
  areCitationListsEqual,
  buildPageLimits,
  createCitationDraftRow,
  validateCitationDraftRows,
} from "./utils/visitCitations";
import { withCopySourceVersionNames } from "./utils/visitCopyProvenance";
import { getVisitDetails } from "./utils/visitDetails";
import { buildVisitFieldSuggestions } from "./utils/visitFieldSuggestions";
import { buildVisitTypeOptions, formatVisitType } from "./utils/visitFormatting";
import { buildNewVisitPrefill, getTopmostVisibleVisit } from "./utils/visitPrefill";
import { groupTherapyVisits, sortVisitsByDate } from "./utils/visitSorting";
import { buildVisitTypeFilterOptions, excludeHiddenVisitTypes } from "./utils/visitTypeFilter";

// Slightly longer than the 4s `card-flash` keyframe in the Tailwind preset so it
// only fires as a fallback when onAnimationEnd never reaches the flashing card.
const FLASH_ANIMATION_FALLBACK_MS = 4300;

export const Visits: React.FC = () => {
  const {
    caseId,
    selectedVersionId: versionId,
    versions,
    setSelectedVersionId,
    template,
    fileMapping,
    caseFiles: availableFiles,
    openVisitCitation,
    citationPanel: { isOpen: isPdfPanelOpen },
    runVersionEdit,
    isFinishedCaseReadOnly,
  } = useMedicalChronology();
  const { searchQuery, setSearchQuery, debouncedSearchQuery } = useMedicalChronologySearch();
  const [, medChronServiceV2] = useMedChronServices();
  const {
    collapseTherapyVisits,
    setCollapseTherapyVisits,
    chronologyViewGroupBy: viewType,
    setChronologyViewGroupBy: setViewType,
    chronologySortField,
    chronologySortDirection,
    setChronologySort,
    visitsDisplayMode,
    setVisitsDisplayMode,
    hiddenVisitTypeIds,
    setHiddenVisitTypeIds,
  } = useUserCustomizationPreferencesStore();

  const formatVisitTypeById = useCallback(
    (id: string) => formatVisitType(template, id),
    [template]
  );
  const collapsibleVisitTypeIds = useMemo(() => getCollapsibleVisitTypeIds(template), [template]);
  const { visits, filteredVisits, isLoading, setVisits } = useVisits(
    caseId,
    versionId,
    debouncedSearchQuery,
    formatVisitTypeById,
    fileMapping
  );
  const fieldSuggestions = useMemo(() => buildVisitFieldSuggestions(visits), [visits]);
  const visitTypeFilterOptions = useMemo(
    () => buildVisitTypeFilterOptions(visits, template),
    [visits, template]
  );
  const displayedVisits = useMemo(
    () =>
      withCopySourceVersionNames(
        excludeHiddenVisitTypes(filteredVisits, hiddenVisitTypeIds),
        versions
      ),
    [filteredVisits, hiddenVisitTypeIds, versions]
  );
  const hasOtherVersions = versions.length > 1;

  const [isEditModeToggledOn, setIsEditModeToggledOn] = useState(false);
  const editMode = isEditModeToggledOn && !isFinishedCaseReadOnly;
  const [editingVisitDetail, setEditingVisitDetail] = useState<EditingVisitDetailState | null>(
    null
  );
  const [editingVisitHeader, setEditingVisitHeader] = useState<string | null>(null);
  const [newDetailCategory, setNewDetailCategory] = useState("");
  const [newDetailText, setNewDetailText] = useState("");
  const [showNewDetailForm, setShowNewDetailForm] = useState<string | null>(null);
  const [isSavingDetail, setIsSavingDetail] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<DeleteDetailConfirm | null>(null);
  const [showDeleteVisitConfirm, setShowDeleteVisitConfirm] = useState<VisitDisplay | null>(null);
  const [bulkDeleteVisitIds, setBulkDeleteVisitIds] = useState<string[] | null>(null);
  const [isBulkDeletingVisits, setIsBulkDeletingVisits] = useState(false);
  const [isBulkEditingVisits, setIsBulkEditingVisits] = useState(false);
  const [bulkCopyVisitIds, setBulkCopyVisitIds] = useState<string[] | null>(null);
  const [isBulkCopyingVisits, setIsBulkCopyingVisits] = useState(false);
  const visitBulkSelectionRef = useRef<{
    clearSelection: () => void;
    removeVisitIds: (ids: Iterable<string>) => void;
  } | null>(null);
  const [targetVisitId, setTargetVisitId] = useState<string | null>(null);
  const [showNewVisitForm, setShowNewVisitForm] = useState(false);
  const [newVisitValues, setNewVisitValues] = useState<NewVisitValues>(() => ({
    visit_date: "",
    visit_type_id: "",
    provider_name: "",
    facility_name: "",
    visit_subtype: "",
    citations: [createCitationDraftRow()],
  }));
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [headerFormErrors, setHeaderFormErrors] = useState<string[]>([]);

  const activeVisitIdRef = useRef<string | null>(null);
  const medChronRef = useRef<HTMLElement | null>(null);
  const getVisitFlatIndex = useRef<((visitId: string) => number | null) | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const anchoredOffset = useRef<number>(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  useFocusSearchOnCmdF(searchInputRef, medChronRef, { disabled: isPdfPanelOpen });

  const handleFlatItemsChange = useCallback((fn: (visitId: string) => number | null) => {
    getVisitFlatIndex.current = fn;
  }, []);

  const handleEditModeRowClick = useCallback(
    (visitId: string) => {
      setVisitsDisplayMode("list");
      setTargetVisitId(visitId);
    },
    [setVisitsDisplayMode]
  );

  const handleFlashComplete = useCallback(() => {
    setTargetVisitId(null);
  }, []);

  useEffect(() => {
    if (!targetVisitId) return;
    const timeoutId = window.setTimeout(() => {
      setTargetVisitId(null);
    }, FLASH_ANIMATION_FALLBACK_MS);
    return () => window.clearTimeout(timeoutId);
  }, [targetVisitId]);

  useEffect(() => {
    if (visitsDisplayMode !== "list") setTargetVisitId(null);
  }, [visitsDisplayMode]);

  const handleNewVisitClick = useCallback(() => {
    const visitReference = buildNewVisitPrefill(
      getTopmostVisibleVisit(medChronRef.current, visits)
    );
    setNewVisitValues(visitReference);
    setShowNewVisitForm(true);
  }, [visits]);

  useEffect(() => {
    const container = document.querySelector(".medical-chronology") as HTMLDivElement;
    if (!container) return;
    medChronRef.current = container;

    const scrollAfterTransition = () => {
      if (activeVisitIdRef.current === null) return;
      const idx = getVisitFlatIndex.current?.(activeVisitIdRef.current);
      if (idx != null) {
        virtuosoRef.current?.scrollToIndex({
          index: idx,
          behavior: "auto",
          align: "start",
          offset: -anchoredOffset.current,
        });
      }
      container.removeEventListener("transitionend", scrollAfterTransition);
    };

    const mutationObserver = new MutationObserver(() => {
      if (activeVisitIdRef.current === null) return;
      const isPanelOpen = container.classList.contains("with-right-panel");
      if (!isPanelOpen) {
        // Panel just closed — scroll before the wider layout paints
        const idx = getVisitFlatIndex.current?.(activeVisitIdRef.current);
        if (idx != null) {
          virtuosoRef.current?.scrollToIndex({
            index: idx,
            behavior: "auto",
            align: "start",
            offset: -anchoredOffset.current,
          });
        }
        container.removeEventListener("transitionend", scrollAfterTransition);
      } else {
        // Panel just opened — wait for CSS transition to settle
        container.removeEventListener("transitionend", scrollAfterTransition);
        container.addEventListener("transitionend", scrollAfterTransition, { once: true });
      }
    });

    mutationObserver.observe(container, { attributes: true, attributeFilter: ["class"] });

    return () => {
      mutationObserver.disconnect();
      container.removeEventListener("transitionend", scrollAfterTransition);
    };
  }, []);

  const handleCitationClick = useCallback(
    (citation: MedVisitCitation, vId?: string) => {
      if (vId) {
        activeVisitIdRef.current = vId;
        const container = medChronRef.current;
        if (container) {
          const el = container.querySelector(`[data-visit-id="${vId}"]`);
          anchoredOffset.current = el
            ? el.getBoundingClientRect().top - container.getBoundingClientRect().top
            : 0;
        }
      }
      void openVisitCitation(citation);
    },
    [openVisitCitation]
  );

  const handleVisitDetailUpdate = async (
    visitId: string,
    oldCategory: string,
    newCategory: string,
    newDetails: string
  ) => {
    if (!versionId || isSavingDetail) return;

    // Check if the detail is already updated. If so, don't make a request to the server.
    const visit = visits.find((v) => v.visit_id === visitId);
    const existingDetail = visit?.med_visit_details.find(
      (d) => d.visit_detail_category === oldCategory
    );
    if (
      existingDetail &&
      areVisitDetailEditsSemanticallyNoop(
        existingDetail.visit_detail_category,
        existingDetail.visit_details,
        newCategory,
        newDetails
      )
    ) {
      setEditingVisitDetail(null);
      return;
    }

    setIsSavingDetail(true);
    try {
      const response = await runVersionEdit(versionId, () =>
        medChronServiceV2.updateVisitDetail(caseId, versionId, visitId, oldCategory, {
          visit_detail_category: newCategory,
          visit_details: newDetails,
        })
      );
      setVisits((prev) =>
        prev.map((v) =>
          v.visit_id === visitId
            ? {
                ...v,
                med_visit_details: v.med_visit_details.map((d) =>
                  d.visit_detail_category === oldCategory ? response : d
                ),
              }
            : v
        )
      );
      setEditingVisitDetail(null);
    } catch (_e) {
      toast.error(getErrorMessage(_e));
    } finally {
      setIsSavingDetail(false);
    }
  };

  const confirmDeleteDetail = async () => {
    if (!showDeleteConfirm || !versionId) return;
    const { visitId, category } = showDeleteConfirm;
    try {
      await runVersionEdit(versionId, () =>
        medChronServiceV2.deleteVisitDetail(versionId, visitId, category)
      );
      setVisits((prev) =>
        prev.map((v) =>
          v.visit_id === visitId
            ? {
                ...v,
                med_visit_details: v.med_visit_details.filter(
                  (d) => d.visit_detail_category !== category
                ),
              }
            : v
        )
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const handleSubmitNewDetail = async (visitId: string) => {
    if (!versionId || !newDetailCategory.trim() || !newDetailText.trim() || isSavingDetail) return;

    const visit = visits.find((v) => v.visit_id === visitId);
    const normalizedCategory = normalizeVisitDetailCategory(newDetailCategory);
    const existingSameCategory = visit?.med_visit_details.find(
      (d) => normalizeVisitDetailCategory(d.visit_detail_category) === normalizedCategory
    );

    if (existingSameCategory) {
      if (
        areVisitDetailEditsSemanticallyNoop(
          existingSameCategory.visit_detail_category,
          existingSameCategory.visit_details,
          newDetailCategory,
          newDetailText
        )
      ) {
        setShowNewDetailForm(null);
        setNewDetailCategory("");
        setNewDetailText("");
        return;
      }

      setIsSavingDetail(true);
      try {
        const response = await runVersionEdit(versionId, () =>
          medChronServiceV2.updateVisitDetail(
            caseId,
            versionId,
            visitId,
            existingSameCategory.visit_detail_category,
            { visit_detail_category: newDetailCategory, visit_details: newDetailText }
          )
        );
        setVisits((prev) =>
          prev.map((v) =>
            v.visit_id === visitId
              ? {
                  ...v,
                  med_visit_details: v.med_visit_details.map((d) =>
                    d.visit_detail_category === existingSameCategory.visit_detail_category
                      ? response
                      : d
                  ),
                }
              : v
          )
        );
        setShowNewDetailForm(null);
        setNewDetailCategory("");
        setNewDetailText("");
      } catch (_e) {
        toast.error(getErrorMessage(_e));
      } finally {
        setIsSavingDetail(false);
      }
      return;
    }

    setIsSavingDetail(true);
    try {
      const response = await runVersionEdit(versionId, () =>
        medChronServiceV2.addVisitDetail(caseId, versionId, visitId, {
          visit_detail_category: newDetailCategory,
          visit_details: newDetailText,
        })
      );
      setVisits((prev) =>
        prev.map((v) =>
          v.visit_id === visitId
            ? { ...v, med_visit_details: [...v.med_visit_details, response] }
            : v
        )
      );
      setShowNewDetailForm(null);
      setNewDetailCategory("");
      setNewDetailText("");
    } catch (_e) {
      toast.error(getErrorMessage(_e));
    } finally {
      setIsSavingDetail(false);
    }
  };

  const confirmDeleteVisit = async () => {
    if (!showDeleteVisitConfirm || !versionId) return;
    const visit = showDeleteVisitConfirm;
    try {
      await runVersionEdit(versionId, () =>
        medChronServiceV2.deleteVisit(versionId, visit.visit_id)
      );
      setVisits((prev) => prev.filter((v) => v.visit_id !== visit.visit_id));
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setShowDeleteVisitConfirm(null);
    }
  };

  const handleRequestBulkDelete = useCallback((visitIds: string[]) => {
    if (visitIds.length === 0) return;
    setBulkDeleteVisitIds(visitIds);
  }, []);

  const confirmBulkDeleteVisits = useCallback(async () => {
    if (!bulkDeleteVisitIds?.length || !versionId) return;
    const idsToDelete = [...new Set(bulkDeleteVisitIds)];
    setIsBulkDeletingVisits(true);
    try {
      const result = await runVersionEdit(versionId, () =>
        medChronServiceV2.bulkDeleteVisits(caseId, versionId, idsToDelete)
      );

      setVisits((prev) => removeDeletedVisitsFromList(prev, result.deleted_visit_ids));
      visitBulkSelectionRef.current?.clearSelection();
      toast.success(
        `Deleted ${result.deleted_count} visit${result.deleted_count !== 1 ? "s" : ""}`
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsBulkDeletingVisits(false);
      setBulkDeleteVisitIds(null);
    }
  }, [bulkDeleteVisitIds, versionId, caseId, medChronServiceV2, setVisits, runVersionEdit]);

  const handleBulkEditSave = useCallback(
    async (visitIds: string[], values: BulkEditSaveValues): Promise<boolean> => {
      if (!versionId || visitIds.length === 0) {
        return false;
      }

      const patch = buildBulkVisitUpdatePayload(values);
      if (!patch) {
        toast.error("Enter at least one field to update");
        return false;
      }

      const uniqueVisitIds = [...new Set(visitIds)];
      setIsBulkEditingVisits(true);
      try {
        const result = await runVersionEdit(versionId, () =>
          medChronServiceV2.bulkUpdateVisits(caseId, versionId, uniqueVisitIds, patch)
        );
        if (result.updated_count === 0) {
          toast("No visits were updated.", { icon: "⚠️" });
          return false;
        }

        if (result.updated_count !== uniqueVisitIds.length) {
          toast.error("Some visits could not be updated. Refresh and try again.");
          return false;
        }

        setVisits((prev) => applyBulkVisitPatchToList(prev, uniqueVisitIds, patch));
        visitBulkSelectionRef.current?.clearSelection();
        toast.success(
          `Updated ${result.updated_count} visit${result.updated_count !== 1 ? "s" : ""}`
        );
        return true;
      } catch (error) {
        toast.error(getErrorMessage(error));
        return false;
      } finally {
        setIsBulkEditingVisits(false);
      }
    },
    [caseId, versionId, medChronServiceV2, setVisits, runVersionEdit]
  );

  const handleBulkCopyClick = useCallback((visitIds: string[]) => {
    if (visitIds.length === 0) return;
    setBulkCopyVisitIds(visitIds);
  }, []);

  const handleBulkCopySave = useCallback(
    async (targetVersionId: string) => {
      if (!bulkCopyVisitIds?.length || !versionId) return;
      const targetVersion = versions.find((v) => v.version_id === targetVersionId);
      if (!targetVersion) {
        toast.error("That version is no longer available. Please choose another version.");
        return;
      }
      const idsToCopy = [...new Set(bulkCopyVisitIds)];
      setIsBulkCopyingVisits(true);
      // Only the request is in the try: a post-success throw must not read as a failed copy, or a retry duplicates visits.
      let result: MedVisitBulkCopyResponse;
      try {
        result = await runVersionEdit(targetVersionId, () =>
          medChronServiceV2.bulkCopyVisits(targetVersionId, versionId, idsToCopy)
        );
      } catch (error) {
        setIsBulkCopyingVisits(false);
        toast.error(getErrorMessage(error));
        return;
      }
      setIsBulkCopyingVisits(false);

      const copiedCount = result.copied_visits.length;
      setBulkCopyVisitIds(null);
      if (!result.forked) setSelectedVersionId(targetVersionId);
      visitBulkSelectionRef.current?.clearSelection();
      const destination = result.forked ? "a new version" : targetVersion.version_name;
      toast.success(`Copied ${copiedCount} visit${copiedCount !== 1 ? "s" : ""} to ${destination}`);
    },
    [bulkCopyVisitIds, versionId, versions, medChronServiceV2, setSelectedVersionId, runVersionEdit]
  );

  const handleAddNewVisit = async () => {
    const required: (keyof NewVisitValues)[] = [
      "visit_date",
      "visit_type_id",
      "provider_name",
      "facility_name",
    ];
    const labels: Record<string, string> = {
      visit_date: "Visit date",
      visit_type_id: "Visit type",
      provider_name: "Provider name",
      facility_name: "Facility name",
    };
    const visitTypeOptions = buildVisitTypeOptions(template);
    const missing = required.filter((key) => {
      if (key === "visit_type_id") {
        return !visitTypeOptions.some((opt) => opt.value === newVisitValues.visit_type_id);
      }
      return !newVisitValues[key];
    });
    const citationValidation = validateCitationDraftRows(
      newVisitValues.citations,
      new Set(availableFiles.map((file) => file.file_id)),
      buildPageLimits(availableFiles)
    );
    const errors = missing.map((key) => labels[key]);
    if (!citationValidation.isValid || citationValidation.citations.length === 0) {
      errors.push(NEW_VISIT_CITATIONS_ERROR_KEY);
    }
    if (errors.length) {
      setFormErrors(errors);
      toast.error(missing.length ? `${labels[missing[0]]} is required` : "Check the citations");
      return;
    }
    if (!versionId) {
      toast.error("No chronology version selected");
      return;
    }
    const visitData: MedVisitCreateRequest = {
      visit_date: newVisitValues.visit_date || null,
      visit_type_id: newVisitValues.visit_type_id,
      provider_name: newVisitValues.provider_name.trim() || null,
      facility_name: newVisitValues.facility_name.trim() || null,
      visit_subtype: newVisitValues.visit_subtype.trim() || null,
      citations: citationValidation.citations,
    };
    try {
      const result = await runVersionEdit(versionId, () =>
        medChronServiceV2.createVisit(versionId, visitData)
      );
      const newVisit: VisitDisplay = {
        ...visitData,
        citations: citationValidation.citations,
        version_id: versionId,
        visit_id: result.visit_id,
        med_visit_details: [],
      };
      setVisits((prev) => sortVisitsByDate([...prev, newVisit]));
      setShowNewVisitForm(false);
      setNewVisitValues({
        visit_date: "",
        visit_type_id: "",
        provider_name: "",
        facility_name: "",
        visit_subtype: "",
        citations: [createCitationDraftRow()],
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleVisitHeaderUpdate = async (visitId: string, values: VisitHeaderSaveValues) => {
    if (!versionId) return;
    if (!values.visit_type_id) {
      setHeaderFormErrors([VISIT_TYPE_ERROR_KEY]);
      toast.error("Visit type is required");
      return;
    }
    setHeaderFormErrors([]);
    const currentCitations = visits.find((v) => v.visit_id === visitId)?.citations;
    const citationsChanged =
      !currentCitations || !areCitationListsEqual(currentCitations, values.citations);
    const updateData: MedVisitUpdateRequest = {
      visit_date: values.visit_date,
      visit_type_id: values.visit_type_id,
      provider_name: values.provider_name.trim(),
      facility_name: values.facility_name.trim(),
      visit_subtype: values.visit_subtype.trim(),
      ...(citationsChanged && { citations: values.citations }),
    };
    try {
      const updatedVisit = await runVersionEdit(versionId, () =>
        medChronServiceV2.updateVisit(versionId, visitId, updateData)
      );
      setVisits((prev) =>
        sortVisitsByDate(prev.map((v) => (v.visit_id === visitId ? { ...v, ...updatedVisit } : v)))
      );
      setEditingVisitHeader(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleEditHeaderClick = (visit: VisitDisplay) => {
    setEditingVisitHeader(visit.visit_id);
    setHeaderFormErrors([]);
  };

  const autoResizeTextarea = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const hasVisitTypeError = headerFormErrors.includes(VISIT_TYPE_ERROR_KEY);

  if (!versionId || !caseId) return null;

  return (
    <>
      <VisitsView
        isVisitsLoading={isLoading && visits.length === 0}
        visits={displayedVisits}
        viewType={viewType}
        setViewType={setViewType}
        chronologySortField={chronologySortField}
        chronologySortDirection={chronologySortDirection}
        setChronologySort={setChronologySort}
        visitTypeFilterOptions={visitTypeFilterOptions}
        hiddenVisitTypeIds={hiddenVisitTypeIds}
        setHiddenVisitTypeIds={setHiddenVisitTypeIds}
        visitsDisplayMode={visitsDisplayMode}
        setVisitsDisplayMode={setVisitsDisplayMode}
        targetVisitId={targetVisitId}
        onEditModeRowClick={handleEditModeRowClick}
        onFlashComplete={handleFlashComplete}
        editMode={editMode}
        setEditMode={setIsEditModeToggledOn}
        isEditModeAvailable={!isFinishedCaseReadOnly}
        collapseTherapyVisits={collapseTherapyVisits}
        setCollapseTherapyVisits={setCollapseTherapyVisits}
        template={template}
        fileMapping={fileMapping}
        onCitationClick={handleCitationClick}
        groupTherapyVisits={(list: VisitDisplay[], preserveOrder?: boolean) =>
          groupTherapyVisits(list, collapseTherapyVisits, collapsibleVisitTypeIds, preserveOrder)
        }
        getVisitDetails={(visit: VisitDisplay) => getVisitDetails(visit, editMode)}
        editingVisitDetail={editingVisitDetail}
        setEditingVisitDetail={setEditingVisitDetail}
        editingVisitHeader={editingVisitHeader}
        newDetailCategory={newDetailCategory}
        setNewDetailCategory={setNewDetailCategory}
        newDetailText={newDetailText}
        setNewDetailText={setNewDetailText}
        showNewDetailForm={showNewDetailForm}
        setShowNewDetailForm={setShowNewDetailForm}
        isSavingDetail={isSavingDetail}
        setShowDeleteConfirm={setShowDeleteConfirm}
        setShowDeleteVisitConfirm={setShowDeleteVisitConfirm}
        handleVisitDetailUpdate={handleVisitDetailUpdate}
        handleSubmitNewDetail={handleSubmitNewDetail}
        handleVisitHeaderUpdate={handleVisitHeaderUpdate}
        handleEditHeaderClick={handleEditHeaderClick}
        onCancelEditHeader={() => {
          setEditingVisitHeader(null);
          setHeaderFormErrors([]);
        }}
        hasVisitTypeError={hasVisitTypeError}
        onClearVisitTypeError={() => setHeaderFormErrors([])}
        autoResizeTextarea={autoResizeTextarea}
        availableFiles={availableFiles}
        fieldSuggestions={fieldSuggestions}
        onNewVisitClick={handleNewVisitClick}
        handleAddNewVisit={handleAddNewVisit}
        scrollContainerRef={medChronRef}
        onFlatItemsChange={handleFlatItemsChange}
        virtuosoRef={virtuosoRef}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        highlightQuery={debouncedSearchQuery}
        searchInputRef={searchInputRef}
        onRequestBulkDelete={handleRequestBulkDelete}
        onRequestBulkCopy={handleBulkCopyClick}
        hasOtherVersions={hasOtherVersions}
        onBulkEditSave={handleBulkEditSave}
        isBulkEditingVisits={isBulkEditingVisits}
        isBulkDeletingVisits={isBulkDeletingVisits}
        isBulkCopyingVisits={isBulkCopyingVisits}
        visitBulkSelectionRef={visitBulkSelectionRef}
      />
      {bulkCopyVisitIds && (
        <VisitsBulkCopyModal
          selectedVisitCount={bulkCopyVisitIds.length}
          versions={versions}
          currentVersionId={versionId}
          isSaving={isBulkCopyingVisits}
          onSave={handleBulkCopySave}
          onClose={() => {
            if (!isBulkCopyingVisits) setBulkCopyVisitIds(null);
          }}
        />
      )}
      <DeleteConfirmModals
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        confirmDeleteDetail={confirmDeleteDetail}
        showDeleteVisitConfirm={showDeleteVisitConfirm}
        setShowDeleteVisitConfirm={setShowDeleteVisitConfirm}
        confirmDeleteVisit={confirmDeleteVisit}
        bulkDeleteVisitIds={bulkDeleteVisitIds}
        setBulkDeleteVisitIds={setBulkDeleteVisitIds}
        confirmBulkDeleteVisits={confirmBulkDeleteVisits}
        isBulkDeletingVisits={isBulkDeletingVisits}
      />
      {showNewVisitForm && (
        <NewVisitFormModal
          newVisitValues={newVisitValues}
          setNewVisitValues={setNewVisitValues}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          template={template}
          availableFiles={availableFiles}
          fileMapping={fileMapping}
          fieldSuggestions={fieldSuggestions}
          onAddVisit={handleAddNewVisit}
          onClose={() => {
            setShowNewVisitForm(false);
            setFormErrors([]);
          }}
        />
      )}
    </>
  );
};
